import { useEffect, useState } from "react";
import axios from "axios";
import {
  FileText, Table2, Languages, ShieldAlert, ClipboardCheck, Activity,
  ChevronRight, ExternalLink, CheckCircle2, XCircle, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useFetch = (path, dep) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    setData(null); setErr(null);
    axios.get(`${API}${path}`).then((r) => live && setData(r.data)).catch((e) => live && setErr(e.message));
    return () => { live = false; };
  }, [path, dep]);
  return { data, err };
};

const Spin = () => <div className="ev-loading" style={{ minHeight: 200 }}><Loader2 className="ev-spin" /> Loading…</div>;

const Badge = ({ children, tone }) => <span className={`ev-badge ${tone || ""}`}>{children}</span>;

const METHOD_LABELS = {
  original: ["ev-badge-green", "original (source)"],
  wiki_published: ["ev-badge-green", "wiki_published"],
  manual: ["ev-badge-green", "manual (curated)"],
  gtranslate_machine: ["ev-badge-amber", "gtranslate (machine)"],
  llm_generated: ["ev-badge-amber", "llm_generated"],
};
const MethodBadge = ({ method }) => {
  const [tone, label] = METHOD_LABELS[method] || ["", method];
  return <span className={`ev-badge ${tone}`}>{label}</span>;
};

// ---------------- Table renderer (structural) ----------------
const DataTable = ({ t }) => (
  <div className="ev-scrolltable">
    {t.caption && <div className="ev-muted ev-small" style={{ marginBottom: 6 }}>{t.caption}</div>}
    <table className="ev-table ev-datatable">
      <thead><tr>{t.headers.map((h, i) => <th key={i}>{h || "—"}</th>)}</tr></thead>
      <tbody>
        {t.rows.slice(0, 8).map((r, i) => (
          <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>
        ))}
      </tbody>
    </table>
    {t.rows.length > 8 && <div className="ev-muted ev-small">+{t.rows.length - 8} more rows</div>}
  </div>
);

// ---------------- Sections renderer ----------------
const Sections = ({ sections }) => (
  <div className="ev-sections">
    {sections.map((s, i) => (
      <div key={i} className="ev-section">
        {s.heading && (
          <div className={`ev-sec-h lvl${s.headingLevel}`}>
            <span className="ev-badge ev-mono">H{s.headingLevel}</span> {s.heading}
          </div>
        )}
        {s.paragraphs?.slice(0, 3).map((p, j) => <p key={j} className="ev-muted ev-small">{p}</p>)}
        {s.paragraphs?.length > 3 && <p className="ev-muted ev-small">…+{s.paragraphs.length - 3} paragraphs</p>}
        {s.lists?.map((l, j) => (
          <ul key={j} className={`ev-parsed-list ${l.listType}`}>
            {l.items.slice(0, 5).map((it, k) => <li key={k} className="ev-small">{it}</li>)}
            {l.items.length > 5 && <li className="ev-muted ev-small">…+{l.items.length - 5}</li>}
          </ul>
        ))}
        {s.tables?.map((t, j) => <DataTable key={j} t={t} />)}
      </div>
    ))}
  </div>
);

// ---------------- Scraped Articles ----------------
function ScrapedArticles() {
  const { data, err } = useFetch("/scrape/articles");
  const [openSlug, setOpenSlug] = useState(null);
  const detail = useFetch(openSlug ? `/scrape/articles/${openSlug}` : "/scrape/summary", openSlug);

  if (err) return <div className="ev-card ev-muted" data-testid="scraped-error">Error: {err}</div>;
  if (!data) return <Spin />;
  if (data.length === 0)
    return <div className="ev-card ev-muted" data-testid="scraped-empty">No scraped articles yet. Run the prototype pipeline.</div>;

  return (
    <div className="ev-list" data-testid="scraped-articles-panel">
      {data.map((a) => {
        const isOpen = openSlug === a.slug;
        return (
          <div key={a.slug} className="ev-acc">
            <button className="ev-acc-h" data-testid={`article-toggle-${a.slug}`}
              onClick={() => setOpenSlug(isOpen ? null : a.slug)}>
              <ChevronRight size={16} className={`ev-chev ${isOpen ? "open" : ""}`} />
              <FileText size={15} />
              <span className="ev-strong">{a.title}</span>
              <span className="ev-mono ev-muted ev-small ev-flex1">{a.slug}</span>
              <Badge>{a.category || "—"}</Badge>
              <Badge tone="ev-badge-gold">{a.availableLanguages?.length || 0} lang stored</Badge>
              <Badge>{a.variantsDetected} variants</Badge>
            </button>
            {isOpen && (
              <div className="ev-acc-b">
                {!detail.data || detail.data.article?.slug !== a.slug ? <Spin /> : (
                  <ArticleDetail d={detail.data} />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ArticleDetail({ d }) {
  const { article, translations } = d;
  const [activeLang, setActiveLang] = useState(translations[0]?.language);
  const active = translations.find((t) => t.language === activeLang) || translations[0];
  return (
    <div>
      <div className="ev-kv">
        <div><span className="ev-muted">articleId (language-neutral)</span><span className="ev-mono">{article.id}</span></div>
        <div><span className="ev-muted">original source language</span><span><Badge tone="ev-badge-green">{article.originalSourceLanguage || "—"}</Badge> (wiki original)</span></div>
        <div><span className="ev-muted">preferred retrieval language</span><span><Badge tone="ev-badge-gold">{article.preferredRetrievalLanguage}</Badge> (fallback)</span></div>
        <div><span className="ev-muted">available languages</span><span>{(article.availableLanguages || []).join(", ") || "—"}</span></div>
        <div><span className="ev-muted">canonical URL</span>
          <a className="ev-link" href={article.canonicalSourceUrl} target="_blank" rel="noreferrer">
            {article.canonicalSourceUrl} <ExternalLink size={11} /></a></div>
        <div><span className="ev-muted">source updated</span><span>{article.sourceUpdatedAt || "—"}</span></div>
        <div><span className="ev-muted">scraped at</span><span>{article.lastScrapedAt}</span></div>
        <div><span className="ev-muted">checksum</span><span className="ev-mono ev-small">{article.checksum}</span></div>
      </div>

      <div className="ev-card-h" style={{ marginTop: 16 }}><Languages size={15} /> Translation variant matches</div>
      <table className="ev-table">
        <thead><tr><th>Lang</th><th>Method</th><th>Conf.</th><th>Fuzzy</th><th>Decision</th><th>Source URL</th></tr></thead>
        <tbody>
          {article.translationMatches?.map((m, i) => (
            <tr key={i} data-testid={`match-${m.language}`}>
              <td><Badge>{m.language}</Badge></td>
              <td className="ev-mono ev-small">{m.matchMethod}</td>
              <td className="ev-mono">{m.confidence}</td>
              <td>{m.isFuzzy ? "yes" : "no"}</td>
              <td>{m.decision === "auto_link"
                ? <Badge tone="ev-badge-green">auto_link</Badge>
                : <Badge tone="ev-badge-amber">{m.decision}</Badge>}</td>
              <td className="ev-mono ev-small ev-truncate">{m.sourceUrl}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="ev-card-h" style={{ marginTop: 16 }}><Table2 size={15} /> Parsed content ({translations.length} language{translations.length > 1 ? "s" : ""})</div>
      <div className="ev-pills">
        {translations.map((t) => (
          <button key={t.language} className={`ev-pill ${activeLang === t.language ? "active" : ""}`}
            data-testid={`trans-pill-${t.language}`} onClick={() => setActiveLang(t.language)}>
            {t.language} <MethodBadge method={t.translationMethod} />
          </button>
        ))}
      </div>
      {active && (
        <div>
          <div className="ev-muted ev-small" style={{ marginBottom: 8 }}>
            <strong>{active.title}</strong> · origin <span className="ev-mono">{active.sourceOrigin}</span> ·
            method <span className="ev-mono">{active.translationMethod}</span> ·
            quality <span className="ev-mono">{active.translationQuality}</span> · {active.sections.length} sections ·{" "}
            {active.sections.reduce((n, s) => n + s.tables.length, 0)} tables
          </div>
          <Sections sections={active.sections} />
        </div>
      )}
    </div>
  );
}

// ---------------- Scrape Runs ----------------
function ScrapeRuns() {
  const { data, err } = useFetch("/scrape/runs");
  if (err) return <div className="ev-card ev-muted">Error: {err}</div>;
  if (!data) return <Spin />;
  if (!data.length) return <div className="ev-card ev-muted" data-testid="runs-empty">No runs yet.</div>;
  const metrics = [
    ["pagesDiscovered", "Discovered"], ["canonicalArticlesSelected", "Selected"],
    ["languageVariantsFound", "Variants"], ["pagesProcessed", "Processed"],
    ["pagesCreated", "Created"], ["pagesUpdated", "Updated"], ["pagesUnchanged", "Unchanged"],
    ["pagesFailed", "Failed"], ["ambiguousMatches", "Ambiguous"], ["skippedByProvenance", "Prov. skipped"],
  ];
  return (
    <div className="ev-list" data-testid="runs-panel">
      {data.map((r) => (
        <div key={r.runId} className="ev-card">
          <div className="ev-card-h"><Activity size={15} /> <span className="ev-mono">{r.runId}</span>
            <Badge tone={r.status.includes("error") ? "ev-badge-amber" : "ev-badge-green"}>{r.status}</Badge></div>
          <div className="ev-metrics">
            {metrics.map(([k, label]) => (
              <div key={k} className="ev-metric" data-testid={`metric-${k}`}>
                <div className="ev-metric-v">{r[k] ?? 0}</div><div className="ev-metric-l">{label}</div>
              </div>
            ))}
          </div>
          <div className="ev-muted ev-small" style={{ marginTop: 10 }}>
            languages: {r.languagesSeen?.join(", ")} · removed: {Object.entries(r.removedContentCounts || {}).map(([k, v]) => `${k}=${v}`).join(", ")}
          </div>
          <div className="ev-muted ev-small">{r.startedAt} → {r.finishedAt}</div>
          {r.errors?.length > 0 && <div className="ev-badge ev-badge-amber" style={{ marginTop: 8 }}>{r.errors.length} errors</div>}
        </div>
      ))}
    </div>
  );
}

// ---------------- Review Queue ----------------
function ReviewQueue() {
  const { data, err } = useFetch("/scrape/review-queue?status=all");
  if (err) return <div className="ev-card ev-muted">Error: {err}</div>;
  if (!data) return <Spin />;
  if (!data.length) return <div className="ev-card ev-muted" data-testid="review-empty">Review queue is empty.</div>;
  const unresolved = data.filter((r) => r.status === "unresolved").length;
  return (
    <div className="ev-list" data-testid="review-panel">
      <div className="ev-muted ev-small" style={{ marginBottom: 8 }}>
        Ambiguous / low-confidence matches are held here and remain UNLINKED until reviewed.
        {" "}<strong>{unresolved}</strong> unresolved · <strong>{data.length - unresolved}</strong> resolved.
      </div>
      {data.map((r) => (
        <div key={r._id} className="ev-card" data-testid={`review-item-${r.siteLanguage}`}>
          <div className="ev-card-h"><ShieldAlert size={15} /> {r.kind}
            <Badge tone={r.status === "resolved" ? "ev-badge-green" : "ev-badge-amber"}>{r.status}</Badge></div>
          <div className="ev-kv">
            <div><span className="ev-muted">article</span><span>{r.canonicalArticleTitle} <span className="ev-mono ev-small">({r.articleSlug})</span></span></div>
            <div><span className="ev-muted">source knowledge language</span><span><Badge>{r.candidateLanguage}</Badge> (site <Badge>{r.siteLanguage}</Badge>) — stored as <strong>{r.candidateLanguage}</strong>, not pt-BR</span></div>
            <div><span className="ev-muted">signal</span><span className="ev-mono ev-small">{r.matchMethod} · conf {r.confidence} · fuzzy {String(r.isFuzzy)}</span></div>
            <div><span className="ev-muted">source URL</span><a className="ev-link" href={r.sourceUrl} target="_blank" rel="noreferrer">{r.sourceUrl}</a></div>
            <div><span className="ev-muted">reason</span><span className="ev-small">{r.reason}</span></div>
            {r.resolution && <div><span className="ev-muted">resolution</span><span className="ev-small">{r.resolution}</span></div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------- Validation (before/after) ----------------
function Validation() {
  const { data, err } = useFetch("/scrape/validation");
  if (err) return <div className="ev-card ev-muted">Error: {err}</div>;
  if (!data) return <Spin />;
  if (!data.length) return <div className="ev-card ev-muted" data-testid="validation-empty">No validation reports yet.</div>;
  return (
    <div className="ev-list" data-testid="validation-panel">
      {data.map((v) => (
        <div key={v._id} className="ev-card" data-testid={`validation-${v.articleSlug}`}>
          <div className="ev-card-h"><ClipboardCheck size={15} /> {v.documentPreview?.title}
            <span className="ev-mono ev-muted ev-small">({v.articleSlug})</span></div>
          <div className="ev-muted ev-small" style={{ marginBottom: 10 }}>
            <a className="ev-link" href={v.sourceUrl} target="_blank" rel="noreferrer">{v.sourceUrl} <ExternalLink size={11} /></a>
            {" · "}detected: <Badge>{v.detectedLanguage?.targetLanguage}</Badge> (site {v.detectedLanguage?.siteLanguage}, html {v.detectedLanguage?.htmlLang})
          </div>
          <div className="ev-ba">
            <div className="ev-ba-col">
              <div className="ev-ba-title"><XCircle size={14} /> Removed</div>
              {Object.entries(v.elementsRemoved || {}).map(([k, val]) => (
                <div key={k} className="ev-ba-row"><span>{k}</span><span className="ev-mono">{val}</span></div>
              ))}
            </div>
            <div className="ev-ba-col">
              <div className="ev-ba-title"><CheckCircle2 size={14} /> Preserved</div>
              {Object.entries(v.originalStructureFound || {}).map(([k, val]) => (
                <div key={k} className="ev-ba-row"><span>{k}</span><span className="ev-mono">{val}</span></div>
              ))}
            </div>
          </div>
          <div className="ev-card-h" style={{ marginTop: 14, fontSize: 14 }}>Final MongoDB document preview</div>
          <div className="ev-kv">
            <div><span className="ev-muted">category</span><span>{v.documentPreview?.category || "—"}</span></div>
            <div><span className="ev-muted">updated</span><span>{v.documentPreview?.updateDate || "—"}</span></div>
            <div><span className="ev-muted">checksum</span><span className="ev-mono ev-small">{v.documentPreview?.checksum}</span></div>
          </div>
          <div className="ev-muted ev-small" style={{ margin: "8px 0" }}>Section headings:</div>
          <div className="ev-badges">
            {v.documentPreview?.sectionHeadings?.map((h, i) => <Badge key={i}>{h}</Badge>)}
          </div>
          {v.documentPreview?.firstTablePreview && (
            <>
              <div className="ev-muted ev-small" style={{ margin: "10px 0 4px" }}>First parsed table (structural):</div>
              <DataTable t={{ ...v.documentPreview.firstTablePreview, caption: null }} />
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Phase2Panel({ view }) {
  if (view === "scraped") return <ScrapedArticles />;
  if (view === "runs") return <ScrapeRuns />;
  if (view === "review") return <ReviewQueue />;
  if (view === "validation") return <Validation />;
  return null;
}
